// ===================================================================
// PA GRANTS — Project Photos Gallery
// Bulk upload, thumbnail gallery with lightbox, tagging
// ===================================================================

import { useState, useRef } from 'react';
import {
  Paper, Text, Group, Stack, Badge, Button, ActionIcon, Modal,
  SimpleGrid, Loader, Image, Textarea,
} from '../../components/gab/GabComponents';
import {
  IconUpload, IconTrash, IconPhoto, IconMaximize, IconMap, IconEdit,
} from '@tabler/icons-react';
import { notifications } from '../../lib/notifications';
import { TABLES, FIELDS, createRecord, updateRecord } from '../../lib/api-switch';
import { uploadFileToV2 } from '../../lib/uploadFile';
import { useAuthStore } from '../../store/authStore';
import { usePAGrantsStore } from '../../store/paGrantsStore';
import type { PADocument } from '../../types/pagrants';

// ── Props ─────────────────────────────────────────────────────

interface ProjectPhotosProps {
  projectId: string;
  organizationId: string;
  documents: PADocument[];
}

// ── Helpers ───────────────────────────────────────────────────

const isImage = (mime: string) =>
  mime.startsWith('image/') || /\.(png|jpg|jpeg|gif|webp|bmp)$/i.test(mime);

// Create a compressed thumbnail data URL from a File object
function createThumbnail(file: File, maxSize = 400): Promise<string> {
  return new Promise((resolve) => {
    const img = new window.Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      let w = img.width, h = img.height;
      if (w > maxSize || h > maxSize) {
        const ratio = Math.min(maxSize / w, maxSize / h);
        w = Math.round(w * ratio);
        h = Math.round(h * ratio);
      }
      canvas.width = w;
      canvas.height = h;
      canvas.getContext('2d')!.drawImage(img, 0, 0, w, h);
      resolve(canvas.toDataURL('image/jpeg', 0.55));
    };
    img.onerror = () => resolve('');
    img.src = URL.createObjectURL(file);
  });
}

// ── Component ─────────────────────────────────────────────────

export default function ProjectPhotos({ projectId, organizationId, documents }: ProjectPhotosProps) {
  const { token } = useAuthStore();
  const { fetchDocuments, deleteDocument } = usePAGrantsStore();

  const fileInputRef = useRef<HTMLInputElement>(null);
  const mapInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadCount, setUploadCount] = useState(0);
  const [uploadTotal, setUploadTotal] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxDoc, setLightboxDoc] = useState<PADocument | null>(null);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [pendingTag, setPendingTag] = useState('damage_photos');
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [uploadDescription, setUploadDescription] = useState('');
  const [editingDoc, setEditingDoc] = useState<PADocument | null>(null);
  const [editDescription, setEditDescription] = useState('');

  // Filter to images; split photos vs maps/sketches by tag
  const allImages = documents.filter(d =>
    isImage(d.mimeType || '') || isImage(d.documentName || '')
  );
  const photos = allImages.filter(d => (d.tags || '') !== 'maps_sketches');
  const maps = allImages.filter(d => (d.tags || '') === 'maps_sketches');

  async function handleBulkUpload(files: File[], tag = 'damage_photos', description = '') {
    if (files.length === 0) return;
    setUploading(true);
    setUploadCount(0);
    setUploadTotal(files.length);

    const FDoc = FIELDS.paDocuments;
    const _today = new Date().toISOString().split('T')[0];
    const user = useAuthStore.getState().user;
    let success = 0;

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      if (!file.type.startsWith('image/')) continue;
      setUploadCount(i + 1);
      try {
        // Create small thumbnail for in-app display (200px, q=0.55 ≈ 15–20KB base64)
        const thumbnail = await createThumbnail(file, 200);

        // Upload file to V2 native storage first (get documentId for download)
        const { attachmentId, documentId } = await uploadFileToV2(file, TABLES.paDocuments);

        // Store thumbnail in notes with DOCID meta marker for download support
        // Pattern: |||DOCID|||{uuid}|||{base64-thumbnail}
        // After store mapper strips the meta prefix, doc.notes = base64 data URL → displayed in grid
        const notesVal = `|||DOCID|||${documentId}|||${thumbnail}`;

        // Create document record with metadata + thumbnail embedded in notes
        const fields: Array<[string, string]> = [
          [FDoc.projectId, projectId],
          [FDoc.organizationId, organizationId],
          [FDoc.title, file.name],
          [FDoc.documentType, 'Photo'],
          [FDoc.uploadedBy, user?.email || ''],
          [FDoc.tags, tag],
          [FDoc.notes, notesVal],
        ];
        await createRecord(TABLES.paDocuments, fields, token ?? undefined);
        success++;
      } catch (err) {
        console.error(`Failed to upload ${file.name}:`, err);
      }
    }

    await fetchDocuments(Number(projectId));
    setUploading(false);
    notifications.show({
      title: 'Photos uploaded',
      message: `${success} of ${files.length} photo${files.length !== 1 ? 's' : ''} uploaded`,
      color: 'green',
    });
  }

  function onFileSelect(e: React.ChangeEvent<HTMLInputElement>) {
    if (e.target.files && e.target.files.length > 0) {
      setPendingFiles(Array.from(e.target.files));
      setPendingTag('damage_photos');
      setUploadDescription('');
      setUploadModalOpen(true);
    }
    e.target.value = '';
  }

  function onMapSelect(e: React.ChangeEvent<HTMLInputElement>) {
    if (e.target.files && e.target.files.length > 0) {
      setPendingFiles(Array.from(e.target.files));
      setPendingTag('maps_sketches');
      setUploadDescription('');
      setUploadModalOpen(true);
    }
    e.target.value = '';
  }

  async function confirmUpload() {
    if (pendingFiles.length === 0) return;
    setUploadModalOpen(false);
    await handleBulkUpload(pendingFiles, pendingTag, uploadDescription);
    setPendingFiles([]);
    setUploadDescription('');
  }

  async function saveDescription() {
    if (!editingDoc) return;
    try {
      await usePAGrantsStore.getState().updateDocument(editingDoc.Rid, { description: editDescription });
      setEditingDoc(null);
      notifications.show({ message: 'Description updated', color: 'green' });
    } catch {
      notifications.show({ message: 'Failed to update description', color: 'red' });
    }
  }

  function openLightbox(doc: PADocument) {
    setLightboxDoc(doc);
    setLightboxOpen(true);
  }

  return (
    <Stack gap="md">
      {/* Hidden multi-file inputs */}
      <input type="file" ref={fileInputRef} style={{ display: 'none' }}
        accept="image/*" multiple onChange={onFileSelect} />
      <input type="file" ref={mapInputRef} style={{ display: 'none' }}
        accept="image/*" multiple onChange={onMapSelect} />

      {/* Upload zone */}
      <Paper
        p="lg" withBorder
        style={{
          border: '2px dashed var(--mantine-color-blue-3)',
          textAlign: 'center',
          cursor: uploading ? 'wait' : 'pointer',
          opacity: uploading ? 0.6 : 1,
        }}
        onClick={() => !uploading && fileInputRef.current?.click()}
      >
        <Stack align="center" gap="xs">
          {uploading ? (
            <>
              <Loader size={32} />
              <Text size="sm" c="dimmed">Uploading {uploadCount} of {uploadTotal}...</Text>
            </>
          ) : (
            <>
              <IconPhoto size={32} color="var(--mantine-color-blue-5)" />
              <Text size="sm" c="dimmed">Click to upload photos — select multiple files at once</Text>
              <Text size="xs" c="dimmed">JPG, PNG, GIF, WebP accepted</Text>
            </>
          )}
        </Stack>
      </Paper>

      {/* Photo count */}
      {photos.length > 0 && (
        <Group justify="space-between">
          <Text size="sm" c="dimmed">{photos.length} photo{photos.length !== 1 ? 's' : ''}</Text>
          <Button size="xs" variant="light" leftSection={<IconUpload size={12} />}
            onClick={() => fileInputRef.current?.click()}
            loading={uploading}>
            Add More
          </Button>
        </Group>
      )}

      {/* Photo grid */}
      {photos.length === 0 ? (
        <Paper p="xl" withBorder ta="center">
          <Stack align="center" gap="sm">
            <IconPhoto size={48} color="var(--mantine-color-gray-4)" />
            <Text c="dimmed">No photos uploaded for this project yet.</Text>
            <Button size="sm" variant="light" leftSection={<IconUpload size={14} />}
              onClick={() => fileInputRef.current?.click()}>
              Upload Photos
            </Button>
          </Stack>
        </Paper>
      ) : (
        <SimpleGrid cols={{ base: 2, sm: 3, md: 4, lg: 5 }} spacing="sm">
          {photos.map(doc => {
            const url = doc.notes?.startsWith('data:') ? doc.notes : '';
            return (
              <Paper key={doc.Rid} withBorder radius="md" style={{ overflow: 'hidden', position: 'relative' }}>
                <div
                  style={{
                    aspectRatio: '4/3',
                    background: 'var(--mantine-color-gray-1)',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden',
                  }}
                  onClick={() => openLightbox(doc)}
                >
                  <Image
                    src={url}
                    alt={doc.documentName}
                    fit="cover"
                    h="100%"
                    w="100%"
                    fallbackSrc="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100'%3E%3Crect fill='%23f0f0f0' width='100' height='100'/%3E%3Ctext x='50' y='55' text-anchor='middle' fill='%23999' font-size='12'%3EPhoto%3C/text%3E%3C/svg%3E"
                  />
                </div>
                <Group justify="space-between" p={6}>
                  <Stack gap={0} style={{ flex: 1, minWidth: 0 }}>
                    <Text size="xs" fw={500} truncate>{doc.documentName}</Text>
                    {doc.description && <Text size="xs" c="dimmed" truncate>{doc.description}</Text>}
                    <Text size="xs" c="dimmed">{doc.uploadDate}</Text>
                  </Stack>
                  <Group gap={2}>
                    <ActionIcon size="xs" variant="subtle" onClick={() => openLightbox(doc)}>
                      <IconMaximize size={12} />
                    </ActionIcon>
                    <ActionIcon size="xs" variant="subtle" onClick={(e) => {
                      e.stopPropagation();
                      setEditingDoc(doc);
                      setEditDescription(doc.description || '');
                    }}>
                      <IconEdit size={12} />
                    </ActionIcon>
                    <ActionIcon size="xs" variant="subtle" color="red"
                      onClick={async (e) => {
                        e.stopPropagation();
                        if (!confirm('Delete this photo?')) return;
                        try {
                          await deleteDocument(doc.Rid);
                          notifications.show({ message: 'Photo deleted', color: 'gray' });
                        } catch (err) {
                          notifications.show({ message: `Delete failed: ${err instanceof Error ? err.message : 'Unknown error'}`, color: 'red' });
                        }
                      }}>
                      <IconTrash size={12} />
                    </ActionIcon>
                  </Group>
                </Group>
              </Paper>
            );
          })}
        </SimpleGrid>
      )}

      {/* ── Maps & Sketches (FEMA 009-0-91C) ──────────────────── */}
      <Paper p="sm" withBorder radius="sm" mt="lg">
        <Group gap="xs" mb="sm">
          <IconMap size={18} color="var(--mantine-color-blue-6)" />
          <Text size="sm" fw={600}>Maps & Sketches</Text>
          <Text size="xs" c="dimmed">(FEMA 009-0-91C)</Text>
        </Group>
        <Text size="xs" c="dimmed" mb="sm">
          Upload site maps, damage sketches, facility drawings, or aerial images documenting the project location and extent of damage.
        </Text>

        {maps.length === 0 ? (
          <Paper
            p="md" withBorder
            style={{ border: '2px dashed var(--mantine-color-blue-3)', textAlign: 'center', cursor: uploading ? 'wait' : 'pointer', opacity: uploading ? 0.6 : 1 }}
            onClick={() => !uploading && mapInputRef.current?.click()}
          >
            <Stack align="center" gap="xs">
              <IconMap size={28} color="var(--mantine-color-blue-4)" />
              <Text size="sm" c="dimmed">Click to upload maps, sketches, or drawings</Text>
            </Stack>
          </Paper>
        ) : (
          <>
            <Group justify="space-between" mb="xs">
              <Text size="xs" c="dimmed">{maps.length} map{maps.length !== 1 ? 's' : ''} / sketch{maps.length !== 1 ? 'es' : ''}</Text>
              <Button size="xs" variant="light" leftSection={<IconUpload size={12} />}
                onClick={() => mapInputRef.current?.click()} loading={uploading}>Add More</Button>
            </Group>
            <SimpleGrid cols={{ base: 2, sm: 3, md: 4 }} spacing="sm">
              {maps.map(doc => {
                const url = doc.notes?.startsWith('data:') ? doc.notes : '';
                return (
                  <Paper key={doc.Rid} withBorder radius="md" style={{ overflow: 'hidden', position: 'relative' }}>
                    <div
                      style={{ aspectRatio: '4/3', background: 'var(--mantine-color-gray-1)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}
                      onClick={() => openLightbox(doc)}
                    >
                      <Image src={url} alt={doc.documentName} fit="cover" h="100%" w="100%"
                        fallbackSrc="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100'%3E%3Crect fill='%23f0f0f0' width='100' height='100'/%3E%3Ctext x='50' y='55' text-anchor='middle' fill='%23999' font-size='12'%3EMap%3C/text%3E%3C/svg%3E" />
                    </div>
                    <Group justify="space-between" p={6}>
                      <Text size="xs" fw={500} truncate style={{ flex: 1, minWidth: 0 }}>{doc.documentName}</Text>
                      <Group gap={2}>
                        <ActionIcon size="xs" variant="subtle" onClick={() => openLightbox(doc)}><IconMaximize size={12} /></ActionIcon>
                        <ActionIcon size="xs" variant="subtle" color="red" onClick={async (e) => {
                          e.stopPropagation();
                          try { await deleteDocument(doc.Rid); notifications.show({ message: 'Map deleted', color: 'gray' }); } catch (err) { notifications.show({ message: `Delete failed: ${err instanceof Error ? err.message : 'Unknown'}`, color: 'red' }); }
                        }}><IconTrash size={12} /></ActionIcon>
                      </Group>
                    </Group>
                  </Paper>
                );
              })}
            </SimpleGrid>
          </>
        )}
      </Paper>

      {/* Upload description modal */}
      <Modal
        opened={uploadModalOpen}
        onClose={() => { setUploadModalOpen(false); setPendingFiles([]); }}
        title={`Upload ${pendingFiles.length} ${pendingTag === 'maps_sketches' ? 'map(s)' : 'photo(s)'}`}
        size="sm"
        centered
      >
        <Stack gap="md">
          <Text size="sm" c="dimmed">
            {pendingFiles.map(f => f.name).join(', ')}
          </Text>
          <Textarea
            label="Description"
            placeholder="Describe the damage, location, or context shown in these photos..."
            value={uploadDescription}
            onChange={(e) => setUploadDescription(e.target.value)}
            minRows={3}
            autosize
          />
          <Group justify="flex-end">
            <Button variant="light" onClick={() => { setUploadModalOpen(false); setPendingFiles([]); }}>Cancel</Button>
            <Button onClick={confirmUpload} leftSection={<IconUpload size={14} />}>Upload</Button>
          </Group>
        </Stack>
      </Modal>

      {/* Edit description modal */}
      <Modal
        opened={!!editingDoc}
        onClose={() => setEditingDoc(null)}
        title={`Edit — ${editingDoc?.documentName || ''}`}
        size="sm"
        centered
      >
        <Stack gap="md">
          <Textarea
            label="Description"
            placeholder="Describe the damage, location, or context..."
            value={editDescription}
            onChange={(e) => setEditDescription(e.target.value)}
            minRows={3}
            autosize
          />
          <Group justify="flex-end">
            <Button variant="light" onClick={() => setEditingDoc(null)}>Cancel</Button>
            <Button onClick={saveDescription}>Save</Button>
          </Group>
        </Stack>
      </Modal>

      {/* Lightbox modal */}
      <Modal
        opened={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
        title={lightboxDoc?.documentName || 'Photo'}
        size="xl"
        centered
      >
        {lightboxDoc && (
          <Stack gap="sm">
            <div style={{ textAlign: 'center', background: 'var(--mantine-color-gray-1)', borderRadius: 8, padding: 8 }}>
              <Image
                src={lightboxDoc.notes?.startsWith('data:') ? lightboxDoc.notes : undefined}
                alt={lightboxDoc.documentName}
                fit="contain"
                mah={600}
                fallbackSrc="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='300'%3E%3Crect fill='%23f0f0f0' width='400' height='300'/%3E%3Ctext x='200' y='155' text-anchor='middle' fill='%23999' font-size='16'%3EImage unavailable%3C/text%3E%3C/svg%3E"
              />
            </div>
            <Group gap="sm">
              <Badge size="sm" variant="light">{lightboxDoc.mimeType}</Badge>
              <Text size="xs" c="dimmed">
                {lightboxDoc.uploadDate} {lightboxDoc.uploadedBy ? `by ${lightboxDoc.uploadedBy}` : ''}
                {lightboxDoc.fileSize ? ` | ${(parseInt(lightboxDoc.fileSize) / 1024).toFixed(0)} KB` : ''}
              </Text>
            </Group>
            {lightboxDoc.description && (
              <Text size="sm" c="dimmed">{lightboxDoc.description}</Text>
            )}
          </Stack>
        )}
      </Modal>
    </Stack>
  );
}
